import * as React from 'react';
import { useObjectRef } from 'react-aria';
import {
    type Control,
    type FieldValues,
    type Path,
    type UseControllerProps,
    useController,
} from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import { useHiveClient } from '@gitbook/hive-react';
import { PalettePositionWidth, typeQueryInPalette, usePaletteHandle } from '@gitbook/react-uikit';
import type { Document } from '@gitbook/slate';
import { renderIcon, TextInput, type TextInputProps } from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { normalizeUrlLikeStringToUrl } from '@gitbook/util-url';

import { useContentReferenceContext } from './context';
import { createInputContentRefPalette } from './createInputContentRefPalette';
import type { UIResolvedContentReference } from './types';
import { useContentReference } from './useContentReference';

/**
 * Input to edit a content reference.
 */
export type ContentReferenceInputProps = {
    value: api.ContentRef | null | undefined;
    onChange: (ref: api.ContentRef | null) => void;
    /**
     * List of accepted references.
     */
    accept?: ContentRefKind[];
    clearable?: boolean;
    document?: Document;
} & Pick<
    TextInputProps,
    | 'size'
    | 'onBlur'
    | 'placeholder'
    | 'autoFocus'
    | 'disabled'
    | 'name'
    | 'feedback'
    | 'aria-invalid'
>;

const DEFAULT_SIZE = 'large';

export type ContentReferenceInputFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues = TFieldValues,
> = {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext, TTransformedValues>;
    rules?: UseControllerProps<TFieldValues, Path<TFieldValues>>['rules'];
    required?: boolean;
} & Omit<
    ContentReferenceInputProps,
    'value' | 'onChange' | 'onBlur' | 'name' | 'feedback' | 'aria-invalid'
>;

export function ContentReferenceInputField<
    TFieldValues extends FieldValues = FieldValues,
    TContext = any,
    TTransformedValues = TFieldValues,
>(props: ContentReferenceInputFieldProps<TFieldValues, TContext, TTransformedValues>) {
    const { name, control, rules, required, ...rest } = props;
    const { field, fieldState } = useController({
        name,
        control,
        rules: rules ?? (required ? { required: 'Required ' } : undefined),
    });

    return (
        <ContentReferenceInput
            {...field}
            {...rest}
            disabled={rest.disabled || field.disabled}
            feedback={
                fieldState.isTouched && fieldState.error?.message
                    ? { type: 'danger', message: fieldState.error.message }
                    : undefined
            }
            aria-invalid={fieldState.invalid || undefined}
        />
    );
}

/**
 * Input that allow to pick a content reference.
 */
export const ContentReferenceInput = React.forwardRef(function ContentReferenceInput(
    props: ContentReferenceInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
): React.ReactElement {
    return (
        <React.Suspense
            fallback={<TextInput disabled value="Loading..." size={props.size ?? DEFAULT_SIZE} />}
        >
            <ContentReferenceInputInner ref={ref} {...props} />
        </React.Suspense>
    );
});

/**
 * Inner component that suspends.
 */
const ContentReferenceInputInner = React.forwardRef(function ContentReferenceInputInner(
    props: ContentReferenceInputProps,
    ref: React.ForwardedRef<HTMLInputElement>
): React.ReactElement {
    const {
        value,
        onChange,
        accept,
        document: slateDocument,
        size = DEFAULT_SIZE,
        onBlur,
        clearable = true,
        ...rest
    } = props;

    // Resolves the content ref, it suspends
    const resolved = useContentReference(value);

    // References to DOM elements
    const inputRef = useObjectRef(ref);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // The string representation of the value provided.
    const strValue = React.useMemo(
        () => getStringFromContentRef(value, resolved),
        [value, resolved]
    );

    // Input value kept synced with the value provided
    // so when the user selects it, it resets
    const [inputValue, setInputValue] = useStateWithDeps(strValue, [strValue]);

    // Reset the input value to its initial state.
    const resetInputValue = () => setInputValue(strValue);

    // We consider the input dirty if the user has typed
    // something difference from the initial value.
    const isDirty = inputValue !== strValue;

    // If dirty, the query is the input value, else it the empty
    // because we want to show the complete list.
    const query = isDirty ? inputValue : '';

    // Called when the user is done editing
    // - The palette is closed and the input is not focused
    const onDone = () => {
        resetInputValue();
        const parsedHref = normalizeUrlLikeStringToUrl(query);
        if (parsedHref) {
            onChange({ kind: 'url', url: parsedHref });
        }
    };

    // Handle to control the palette.
    const paletteHandle = usePaletteHandle(containerRef, {
        onClose: () => {
            // If the input is not focused, reset.
            if (document.activeElement !== inputRef.current) {
                onDone();
            }
        },
        inputRef,
    });

    // Dependencies to create the palette.
    const hiveClient = useHiveClient();
    const contentReferenceContext = useContentReferenceContext();

    // Set a query into the palette, open it if not opened.
    const setPaletteQuery = (query: string) => {
        if (!paletteHandle.opened) {
            const isTypingURL = query.startsWith('http') || query.startsWith('www');
            paletteHandle.open(
                typeQueryInPalette(
                    createInputContentRefPalette(
                        onChange,
                        slateDocument
                            ? { ...contentReferenceContext, document: slateDocument }
                            : contentReferenceContext,
                        hiveClient,
                        {
                            search: { enabled: false },
                            position: { width: PalettePositionWidth.ANCHOR },
                            accept: isTypingURL ? ['url'] : accept,
                            value,
                        }
                    ),
                    query
                )
            );
        } else {
            paletteHandle.typeQuery(query);
        }
    };

    return (
        <div ref={containerRef} className="flex flex-col gap-1">
            <TextInput
                ref={inputRef}
                data-testid="contentRef.input"
                size={size}
                value={inputValue}
                onChange={(event) => {
                    const input = event.target.value;
                    setInputValue(input);
                    setPaletteQuery(input !== strValue ? input : '');
                }}
                onFocus={() => {
                    setPaletteQuery(query);
                }}
                onBlur={(event) => {
                    if (!paletteHandle.opened) {
                        onDone();
                    }
                    onBlur?.(event);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setPaletteQuery(query);
                    }
                }}
                onClear={
                    clearable && value
                        ? () => {
                              setInputValue('');
                              onChange(null);
                              inputRef.current?.focus();
                          }
                        : undefined
                }
                prefix={
                    value && !isDirty && resolved.icon ? (
                        <span className="flex items-center text-secondary">
                            {renderIcon(resolved.icon, {
                                size: 16,
                                className: 'text-secondary',
                            })}
                        </span>
                    ) : undefined
                }
                {...rest}
            />
        </div>
    );
});

/**
 * Get a string value from a content ref.
 */
function getStringFromContentRef(
    contentRef: api.ContentRef | null | undefined,
    resolved: UIResolvedContentReference
) {
    if (!contentRef) {
        return '';
    }

    if (contentRef.kind === 'anchor' || contentRef.kind === 'url') {
        return resolved.href;
    }

    return resolved.title;
}
